import React from "react";
import { MessageCircle, Phone, Heart, ShieldCheck, Clock } from "lucide-react";
import { CLINIC, waLink, telLink } from "../lib/contact";

const HERO_IMAGE =
  "https://images.pexels.com/photos/6234612/pexels-photo-6234612.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940";

export const Hero = () => {
  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative pt-28 sm:pt-32 lg:pt-36 pb-16 sm:pb-24 overflow-hidden"
    >
      {/* Decorative blobs */}
      <div
        aria-hidden="true"
        className="absolute -top-24 -left-24 w-[28rem] h-[28rem] bg-[#E3F0E6] blob-shape opacity-70 -z-0"
      />
      <div
        aria-hidden="true"
        className="absolute top-40 -right-20 w-72 h-72 bg-[#F28C8C]/15 blob-shape-2 -z-0"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Text */}
        <div className="lg:col-span-7 fade-up">
          {/* Tag pill */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#D1E2D6] shadow-sm mb-6">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF5A5F] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#FF5A5F]"></span>
            </span>
            <span className="text-xs sm:text-sm font-bold text-[#2D3A30] tracking-wide">
              Urgencias 24/7 · La Serena
            </span>
          </div>

          <h1
            data-testid="hero-title"
            className="font-heading font-semibold text-[#2D3A30] text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
          >
            Cuidamos a tu mascota
            <span className="block mt-2">
              como parte de la{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-[#F28C8C]">familia.</span>
                <span
                  aria-hidden="true"
                  className="absolute inset-x-0 bottom-1 h-3 bg-[#F28C8C]/20 -rotate-1 rounded-full -z-0"
                />
              </span>
            </span>
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-[#4A5E4F]"
          >
            En <strong className="text-[#2D3A30]">Royal Pets Veterinaria</strong>{" "}
            entregamos atención médica veterinaria con cariño, profesionalismo y
            disponibilidad las 24 horas, todos los días. Tu mejor amigo siempre
            estará en buenas manos.
          </p>

          {/* CTAs */}
          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <a
              href={waLink(CLINIC.whatsappEmergencyMessage)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="group inline-flex items-center justify-center gap-3 px-6 py-4 rounded-full bg-[#25D366] hover:bg-[#1EBE5A] text-white font-bold text-base shadow-[0_10px_30px_rgba(37,211,102,0.4)] hover:shadow-[0_14px_36px_rgba(37,211,102,0.55)] hover:-translate-y-0.5 active:scale-95 transition-all duration-300"
            >
              <MessageCircle className="w-5 h-5 group-hover:rotate-[-8deg] transition-transform" />
              Escríbenos por WhatsApp
            </a>
            <a
              href={telLink}
              data-testid="hero-call-btn"
              className="inline-flex items-center justify-center gap-3 px-6 py-4 rounded-full bg-white text-[#2D3A30] border-2 border-[#D1E2D6] hover:border-[#84B891] hover:bg-[#F5F8F5] font-bold text-base hover:-translate-y-0.5 transition-all duration-300"
            >
              <Phone className="w-5 h-5 text-[#F28C8C]" />
              {CLINIC.phoneDisplay}
            </a>
          </div>

          {/* Trust badges */}
          <div className="mt-10 grid grid-cols-3 gap-3 sm:gap-6 max-w-xl">
            {[
              { icon: Clock, label: "Atención", value: "24/7" },
              { icon: Heart, label: "Trato", value: "Cariñoso" },
              { icon: ShieldCheck, label: "Equipo", value: "Profesional" },
            ].map(({ icon: Icon, label, value }) => (
              <div
                key={label}
                className="bg-white/70 backdrop-blur rounded-2xl border border-[#D1E2D6] p-3 sm:p-4 text-center"
              >
                <Icon className="w-5 h-5 mx-auto text-[#84B891]" />
                <div className="font-heading font-semibold text-[#2D3A30] mt-1 text-sm sm:text-base">
                  {value}
                </div>
                <div className="text-[10px] sm:text-xs text-[#7B9681] uppercase tracking-wider font-semibold">
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Image */}
        <div className="lg:col-span-5 relative fade-up" style={{ animationDelay: "120ms" }}>
          <div className="relative">
            <div
              aria-hidden="true"
              className="absolute -inset-4 sm:-inset-6 bg-[#E3F0E6] blob-shape -z-0"
            />
            <img
              src={HERO_IMAGE}
              alt="Veterinaria atendiendo con cariño a un perro"
              className="relative w-full aspect-[4/5] object-cover blob-shape shadow-[0_24px_60px_rgba(45,58,48,0.15)]"
              loading="eager"
            />

            {/* Floating cards */}
            <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-white rounded-2xl shadow-[0_12px_30px_rgba(45,58,48,0.12)] px-4 py-3 flex items-center gap-3 border border-[#D1E2D6] paw-bounce">
              <div className="w-10 h-10 rounded-full bg-[#FF5A5F]/15 flex items-center justify-center">
                <Heart className="w-5 h-5 text-[#FF5A5F]" fill="#FF5A5F" />
              </div>
              <div className="leading-tight">
                <div className="text-xs text-[#7B9681] font-semibold uppercase tracking-wider">
                  Mascotas felices
                </div>
                <div className="font-heading font-semibold text-[#2D3A30]">
                  +1.000 atendidas
                </div>
              </div>
            </div>

            <div
              className="absolute -top-4 -right-2 sm:-right-6 bg-[#2D3A30] text-white rounded-2xl shadow-lg px-4 py-3 flex items-center gap-3 paw-bounce"
              style={{ animationDelay: "0.6s" }}
            >
              <Clock className="w-5 h-5 text-[#F28C8C]" />
              <div className="leading-tight">
                <div className="text-[10px] text-[#A6BFAB] font-semibold uppercase tracking-widest">
                  Disponible
                </div>
                <div className="font-heading font-semibold">24/7</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
